import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useBreakpoint } from "../hooks/useBreakpoint";

/* ─── Design tokens — identical set to CustomerSettingsPage.jsx (T) so
   this dropdown reads as the same product, not a bolted-on component. ─── */
const T = {
  green:       "#1e9254",
  greenDark:   "#155c36",
  greenDeep:   "#0d2e1a",
  greenTint:   "#eef8f3",
  greenBd:     "#a7d9bc",

  bg:          "#f2f6f3",
  white:       "#ffffff",
  card2:       "#f7f8fa",
  border:      "#e4e7ef",
  borderFaint: "#eef0f4",
  hoverBg:     "#F3F4F6",

  text:        "#0f1117",
  textMid:     "#2d3549",
  sub:         "#4a5268",
  faint:       "#8b93a7",
  ghost:       "#c7cddb",

  amber:       "#92400e",
  amberTint:   "#fefaf1",
  amberBd:     "#f3d99a",

  red:         "#b3261e",
  redTint:     "#fdf3f2",
  redBd:       "#f0c9c5",

  blue:        "#1d4ed8",
  blueTint:    "#eef3fd",
  blueBd:      "#c7d7f7",

  gold:        "#b8862f",
  goldTint:    "#fbf5e9",
  goldBd:      "#e9d5a3",

  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",

  rFull: "9999px",
  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1)   return "now";
  if (mins < 60)  return `${mins}m ago`;
  if (hrs < 24)   return `${hrs}h ago`;
  if (days === 1) return "yesterday";
  return new Date(ts).toLocaleDateString("en-KE", { month: "short", day: "numeric" });
}

/* ─── Icon map for notification types — retokened onto the same
   green/blue/amber/gold/red tint pairing used by <Badge> and the
   settings-page section icons, stroke-only (no filled glyphs). ─── */
function NotifIcon({ type }) {
  const styles = {
    booking_accepted:  { bg: T.greenTint, fg: T.green, bd: T.greenBd, path: "M20 6L9 17l-5-5" },
    booking_completed: { bg: T.blueTint,  fg: T.blue,  bd: T.blueBd,  path: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
    new_message:       { bg: T.goldTint,  fg: T.gold,  bd: T.goldBd,  path: "M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" },
    new_review:        { bg: T.amberTint, fg: T.amber, bd: T.amberBd, path: "M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" },
    payment_received:  { bg: T.greenTint, fg: T.green, bd: T.greenBd, path: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
  };
  const s = styles[type] || { bg: T.card2, fg: T.faint, bd: T.border, path: "M15 17H20L18.6 15.6A1 1 0 0118 14.8V11a6 6 0 00-5-5.9V5a1 1 0 00-2 0v.1A6 6 0 006 11v3.8a1 1 0 01-.6.8L4 17H9" };

  return (
    <div style={{
      width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
      background: s.bg, border: `1px solid ${s.bd}`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
        stroke={s.fg} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d={s.path} />
      </svg>
    </div>
  );
}

export default function NotificationsDropdown() {
  const [open, setOpen]               = useState(false);
  const [notifications, setNotifs]    = useState([]);
  const [loading, setLoading]         = useState(false);
  const ref                           = useRef(null);
  const channelRef                    = useRef(null);
  const navigate                      = useNavigate();
  const { isMobile }                  = useBreakpoint();
  const { user, isLoggedIn }          = useAuth();

  const unreadCount = notifications.filter(n => !n.read).length;
  const cappedBadge = Math.min(unreadCount, 9);

  /* ── Fetch notifications ── */
  const fetchNotifs = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      setNotifs(data || []);
    } catch (err) {
      console.error("NotificationsDropdown fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchNotifs();
  }, [isLoggedIn, fetchNotifs]);

  /* ── Realtime subscription ── */
  useEffect(() => {
    if (!user?.id) return;
    channelRef.current = supabase
      .channel(`notifs-badge-${user.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${user.id}`,
      }, () => fetchNotifs())
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${user.id}`,
      }, () => fetchNotifs())
      .subscribe();

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [user?.id, fetchNotifs]);

  /* ── Close on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Close on Escape ── */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  /* ── Mark all read ── */
  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (!unreadIds.length) return;
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    try {
      await supabase
        .from("notifications")
        .update({ read: true })
        .in("id", unreadIds);
    } catch (err) {
      console.error("markAllRead error:", err);
    }
  };

  /* ── Mark single read ── */
  const markRead = async (id) => {
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      await supabase.from("notifications").update({ read: true }).eq("id", id);
    } catch {}
  };

  const handleClick = (n) => {
    markRead(n.id);
    setOpen(false);
    if (n.link) {
      navigate(n.link);
    } else {
      navigate("/notifications");
    }
  };

  const buttonSize = isMobile ? 27 : 34;
  const iconSize   = isMobile ? 12 : 14;

  if (!isLoggedIn) return null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* ── Bell button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Notifications"
        style={{
          width: buttonSize, height: buttonSize, borderRadius: "50%",
          background: T.goldTint, border: `1px solid ${T.goldBd}`,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", padding: 0,
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          fontFamily: T.sans,
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(184,134,47,0.22)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
      >
        <svg width={iconSize} height={iconSize}
          viewBox="0 0 24 24" fill="none"
          stroke={T.gold} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 17H20L18.6 15.6A1 1 0 0118 14.8V11a6 6 0 00-5-5.9V5a1 1 0 00-2 0v.1A6 6 0 006 11v3.8a1 1 0 01-.6.8L4 17H9" />
          <path d="M13 17v1a2 2 0 01-4 0v-1" />
        </svg>

        {/* Numeric badge */}
        {cappedBadge > 0 && (
          <span style={{
            position: "absolute",
            top: isMobile ? -2 : -3,
            right: isMobile ? -2 : -3,
            minWidth: isMobile ? 13 : 15, height: isMobile ? 13 : 15,
            borderRadius: T.rFull,
            background: T.red,
            border: "1.5px solid #fff",
            color: "#fff",
            fontSize: isMobile ? 8 : 9, fontWeight: 700,
            fontFamily: T.sans,
            display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1,
            pointerEvents: "none",
          }}>
            {cappedBadge > 9 ? "9+" : cappedBadge}
          </span>
        )}
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div style={{
          position: isMobile ? "fixed" : "absolute",
          top: isMobile ? 64 : "calc(100% + 10px)",
          left: isMobile ? 16 : "auto",
          right: isMobile ? 16 : 0,
          width: isMobile ? "auto" : 340,
          maxWidth: isMobile ? "none" : 340,
          background: T.white,
          border: `1.5px solid ${T.border}`,
          borderRadius: 14,
          boxShadow: "0 8px 32px rgba(13,46,26,0.14), 0 2px 8px rgba(13,46,26,0.05)",
          zIndex: 9999,
          overflow: "hidden",
          fontFamily: T.sans,
          animation: "notifDropIn 0.17s cubic-bezier(.4,0,.2,1) both",
        }}>
          <style>{`
            @keyframes notifDropIn {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px 10px",
            borderBottom: `1px solid ${T.borderFaint}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: T.sans }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  color: T.green,
                  background: T.greenTint,
                  border: `1px solid ${T.greenBd}`,
                  padding: "1px 7px", borderRadius: T.rFull,
                }}>
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  fontSize: 11, color: T.sub, fontWeight: 600,
                  background: "transparent", border: "none", cursor: "pointer",
                  fontFamily: T.sans, padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 360, overflowY: "auto" }} className="cs-scroll">
            {loading && notifications.length === 0 ? (
              [1, 2, 3].map(i => (
                <div key={i} style={{ display: "flex", gap: 10, padding: "11px 16px", alignItems: "center" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: T.card2, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 10, width: "55%", background: T.card2, borderRadius: 4, marginBottom: 6 }} />
                    <div style={{ height: 9, width: "75%", background: T.card2, borderRadius: 4 }} />
                  </div>
                </div>
              ))
            ) : notifications.length === 0 ? (
              <div style={{ padding: "36px 20px", textAlign: "center" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                  stroke={T.ghost} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
                  style={{ margin: "0 auto 10px", display: "block" }}>
                  <path d="M15 17H20L18.6 15.6A1 1 0 0118 14.8V11a6 6 0 00-5-5.9V5a1 1 0 00-2 0v.1A6 6 0 006 11v3.8a1 1 0 01-.6.8L4 17H9" />
                  <path d="M13 17v1a2 2 0 01-4 0v-1" />
                </svg>
                <p style={{ margin: 0, fontWeight: 600, color: T.sub, fontSize: 14, fontFamily: T.sans }}>
                  No notifications yet
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.faint, lineHeight: 1.5, fontFamily: T.sans }}>
                  Booking updates and messages will appear here.
                </p>
              </div>
            ) : (
              notifications.slice(0, 8).map((n, i) => {
                const isLast = i === Math.min(notifications.length, 8) - 1;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className="cs-dropdown-item"
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 10,
                      padding: "11px 16px",
                      background: !n.read ? T.greenTint : T.white,
                      borderBottom: isLast ? "none" : `1px solid ${T.borderFaint}`,
                      borderLeft: `3px solid ${!n.read ? T.green : "transparent"}`,
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                    }}
                  >
                    <NotifIcon type={n.type} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6, marginBottom: 2 }}>
                        <span style={{
                          fontSize: 12, fontWeight: !n.read ? 700 : 600,
                          color: T.text, fontFamily: T.sans,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {n.title || "Notification"}
                        </span>
                        <span style={{ fontSize: 10, color: T.faint, whiteSpace: "nowrap", flexShrink: 0, fontFamily: T.sans }}>
                          {timeAgo(n.created_at)}
                        </span>
                      </div>
                      <p style={{
                        margin: 0, fontSize: 12,
                        color: !n.read ? T.textMid : T.sub,
                        fontFamily: T.sans,
                        lineHeight: 1.4,
                        overflow: "hidden", textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        fontWeight: !n.read ? 500 : 400,
                      }}>
                        {n.message || n.body || ""}
                      </p>
                    </div>

                    {!n.read && (
                      <div style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: T.green,
                        marginTop: 4, flexShrink: 0,
                      }} />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.borderFaint}`, textAlign: "center" }}>
              <button
                onClick={() => { setOpen(false); navigate("/notifications"); }}
                style={{
                  fontSize: 12, fontWeight: 600,
                  color: T.green,
                  background: "transparent", border: "none",
                  cursor: "pointer", fontFamily: T.sans,
                }}
              >
                View all notifications →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}